import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SUPPORT_PHONE = "09 72 15 80 90";
const SUPPORT_PHONE_HREF = "+33972158090";
const SUPPORT_HOURS = "8h – 23h, tous les jours";
const SUPPORT_LOCATION = "Beaussais-Sur-Mer (22), Bretagne";

const TOPICS = [
  { v: "booking", l: "Réservation voyageur" },
  { v: "owner", l: "Question hôte / propriétaire" },
  { v: "payment", l: "Paiement / facturation" },
  { v: "partner", l: "Devenir partenaire (conciergerie / ménage)" },
  { v: "other", l: "Autre" },
];

export default function SupportPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "booking", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Merci de remplir tous les champs obligatoires");
      return;
    }
    setSending(true);
    try {
      await api.post("/support/contact", form);
      setSent(true);
      toast.success("Message envoyé — réponse sous 24h");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur, merci de réessayer");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-24 sm:pt-28 min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        {/* Header */}
        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">Support</div>
        <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl">
          Une équipe bretonne <em className="italic font-light">à votre écoute.</em>
        </h1>
        <p className="mt-6 text-lg text-neutral-600 max-w-2xl">
          Tous les appels sont traités <strong>depuis la Bretagne</strong>, à Beaussais-Sur-Mer (22). Que vous soyez voyageur ou propriétaire, nous répondons en français — et en breton si vous le souhaitez.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 mt-16">
          {/* Left: contact info */}
          <div className="lg:col-span-2 space-y-8">
            <a
              href={`tel:${SUPPORT_PHONE_HREF}`}
              data-testid="support-phone-link"
              className="block group border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white p-7 hover:bg-[#1B2A49] transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white text-[#0A0A0A] flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/70 font-bold">Assistance téléphonique</div>
                  <div className="font-display text-3xl sm:text-4xl font-bold mt-2 tracking-tight" data-testid="support-phone">
                    {SUPPORT_PHONE}
                  </div>
                  <div className="text-sm text-white/85 mt-3 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {SUPPORT_HOURS}</div>
                  <div className="text-xs text-white/60 mt-1 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {SUPPORT_LOCATION}</div>
                </div>
              </div>
            </a>

            <div className="border border-black/10 p-6">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Email</div>
                  <a href="mailto:contact@monsejourenbretagne.fr" className="font-semibold text-sm hover:underline">contact@monsejourenbretagne.fr</a>
                  <p className="text-xs text-neutral-600 mt-2">Réponse sous 24h ouvrées.</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold">À qui s'adresse notre support ?</div>
              {[
                { t: "Voyageurs", d: "Avant, pendant et après votre séjour. Annulations, modifications, urgences sur place." },
                { t: "Propriétaires", d: "Mise en ligne, calendrier, virements Stripe, gestion des avis, fiscalité." },
                { t: "Partenaires", d: "Conciergeries, prestataires ménage et autres pros qui souhaitent rejoindre notre réseau." },
              ].map((b) => (
                <div key={b.t} className="border-b border-black/10 pb-3">
                  <div className="font-display text-base font-bold">{b.t}</div>
                  <div className="text-xs text-neutral-600 mt-1 leading-relaxed">{b.d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: contact form */}
          <div className="lg:col-span-3">
            {sent ? (
              <div className="border-2 border-[#0A0A0A] p-12 text-center" data-testid="support-sent">
                <div className="w-16 h-16 bg-[#0A0A0A] text-white flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <div className="font-display text-3xl font-bold">Trugarez !</div>
                <p className="text-neutral-600 mt-3 max-w-md mx-auto">
                  Votre message est arrivé chez nous. Un membre de l'équipe basé à Beaussais-Sur-Mer vous répond sous 24h ouvrées.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", topic: "booking", message: "" }); }}
                  className="mt-6 text-sm underline underline-offset-4 font-semibold"
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-black/10 p-7 space-y-4" data-testid="support-form">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Vous écrire</div>
                  <h2 className="font-display text-2xl font-bold mt-1">Envoyez-nous un mot</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Nom *</div>
                    <input data-testid="sup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Email *</div>
                    <input data-testid="sup-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Téléphone</div>
                    <input data-testid="sup-phone" type="tel" placeholder="+33 6 12 34 56 78" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Sujet</div>
                    <select data-testid="sup-topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A] bg-white">
                      {TOPICS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                  </label>
                </div>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Votre message *</div>
                  <textarea data-testid="sup-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Décrivez votre demande en quelques lignes…" className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                </label>
                <button
                  type="submit"
                  data-testid="sup-submit"
                  disabled={sending}
                  className="bg-[#0A0A0A] text-white px-7 py-3.5 font-semibold hover:bg-[#1B2A49] transition-colors flex items-center gap-2 disabled:opacity-60"
                >
                  {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Send className="w-4 h-4" /> Envoyer le message</>}
                </button>
                <p className="text-[11px] text-neutral-500">
                  Vos données sont uniquement utilisées pour répondre à votre demande, jamais transmises à des tiers.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
